import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getFolders, addAggregatorFolderExclusion } from '../../services/api'
import type { Folder } from '../../types'

interface Props {
  projectId: string
  aggregatorId: string
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

export default function HideFolderPicker({ projectId, aggregatorId, isOpen, onClose, onSuccess }: Props) {
  const queryClient = useQueryClient()
  const [selectedFolderIds, setSelectedFolderIds] = useState<string[]>([])

  const { data: folders = [], isLoading } = useQuery({
    queryKey: ['folders', projectId],
    queryFn: () => getFolders(projectId),
    enabled: isOpen,
  })

  const hideMutation = useMutation({
    mutationFn: () => addAggregatorFolderExclusion(projectId, aggregatorId, selectedFolderIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aggregator-grants', projectId] })
      setSelectedFolderIds([])
      onSuccess()
    },
  })

  const toggleFolder = (folderId: string) => {
    setSelectedFolderIds((prev) =>
      prev.includes(folderId) ? prev.filter((id) => id !== folderId) : [...prev, folderId]
    )
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="hide-folder-dialog">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <h3 className="text-base font-semibold text-gray-900 mb-1">Hide Folders</h3>
        <p className="text-sm text-gray-500 mb-4">
          Select folders to exclude from this aggregator's view.
        </p>

        {isLoading && (
          <p className="text-sm text-gray-500 mb-4" data-testid="folders-loading">
            Loading folders…
          </p>
        )}

        {!isLoading && folders.length === 0 && (
          <p className="text-sm text-gray-500 mb-4" data-testid="no-folders">
            No folders found in this project.
          </p>
        )}

        {!isLoading && folders.length > 0 && (
          <ul className="max-h-64 overflow-y-auto space-y-1 mb-4" data-testid="folder-list">
            {folders.map((folder: Folder) => (
              <li key={folder.folder_id}>
                <label className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedFolderIds.includes(folder.folder_id)}
                    onChange={() => toggleFolder(folder.folder_id)}
                    className="rounded border-gray-300"
                    data-testid={`folder-checkbox-${folder.folder_id}`}
                  />
                  <span className="text-sm text-gray-800">{folder.name}</span>
                </label>
              </li>
            ))}
          </ul>
        )}

        {hideMutation.isError && (
          <p className="mb-3 text-sm text-red-600" data-testid="hide-folder-error">
            Failed to hide folders. Please try again.
          </p>
        )}

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            data-testid="hide-folder-cancel"
          >
            Cancel
          </button>
          <button
            onClick={() => hideMutation.mutate()}
            disabled={selectedFolderIds.length === 0 || hideMutation.isPending}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="hide-folder-submit"
          >
            {hideMutation.isPending ? 'Hiding…' : `Hide ${selectedFolderIds.length > 0 ? selectedFolderIds.length : ''} Folder${selectedFolderIds.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  )
}
